import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useSession } from "../SessionContext";
import { useChat } from "../context/ChatContext";
import supabase from "../utils/supabase";
import PostComponent from "../components/Post";
import getAvatarUrl from "../utils/dicebear";
const PublicProfile = () => {
  const { userId } = useParams();
  const [profile, setProfile] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const session = useSession();
  const { createChat } = useChat();

  useEffect(() => {
    const fetchProfileAndPosts = async () => {
      try {
        // Fetch profile
        const { data: profileData, error: profileError } = await supabase
          .from("public_profiles")
          .select("id, name, country, user_type, is_verified")
          .eq("id", userId)
          .single();

        if (profileError) throw profileError;
        setProfile(profileData);

        // Fetch posts
        const { data: postsData, error: postsError } = await supabase
          .from("posts")
          .select(
            `
            *,
            user_info:public_profiles(name, role)
          `
          )
          .eq("user_id", userId)
          .order("created_at", { ascending: false });

        if (postsError) throw postsError;
        setPosts(postsData);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfileAndPosts();
  }, [userId]);

  const handleMessage = async () => {
    try {
      // Create or get existing chat
      const chat = await createChat(session.user.id, userId);
      navigate(`/chat/${chat.id}`);
    } catch (error) {
      console.error("Error creating chat:", error);
    }
  };

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );

  if (!profile)
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl text-gray-600">Profile not found</div>
      </div>
    );

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Profile Header */}
      <div className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="flex flex-col items-center md:flex-row md:items-start gap-6">
            <img
              src={getAvatarUrl(profile.name)}
              alt={profile.name}
              className="w-32 h-32 rounded-full border-4 border-white shadow-lg"
            />

            <div className="flex-1 text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3">
                <h1 className="text-3xl font-bold">{profile.name}</h1>
                {profile.is_verified === "true" && (
                  <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                    Verified
                  </span>
                )}
              </div>

              <div className="mt-2 text-lg text-gray-600">
                {profile.user_type}
              </div>
              <div className="mt-1 text-gray-500">
                <span className="inline-flex items-center">
                  📍 {profile.country}
                </span>
              </div>

              {/* Add message button if not viewing own profile */}
              {session?.user?.id !== userId && (
                <button
                  onClick={handleMessage}
                  className="mt-4 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Message
                </button>
              )}

              <div className="mt-4 flex flex-wrap gap-2 justify-center md:justify-start">
                <span
                  className={`px-3 py-1 rounded-full text-sm
                  ${
                    profile.is_verified === "true"
                      ? "bg-green-100 text-green-800"
                      : profile.is_verified === "pending"
                      ? "bg-yellow-100 text-yellow-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {profile.is_verified === "true"
                    ? "Verified Account"
                    : profile.is_verified === "pending"
                    ? "Verification Pending"
                    : "Not Verified"}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Posts Section */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h2 className="text-2xl font-bold mb-6">Posts</h2>
        {posts.length === 0 ? (
          <div className="text-center text-gray-500 py-8">No posts yet</div>
        ) : (
          <div className="space-y-6">
            {posts.map((post) => (
              <PostComponent
                key={post.id}
                id={post.id}
                name={post.user_info?.name || "Anonymous User"}
                role={post.user_info?.role || "Unknown Role"}
                ago={new Date(post.created_at).toLocaleTimeString()}
                projectDeck="#"
                post={post.content}
                likes={post.likes || 0}
                comments={0}
                title={post.title || "Untitled Post"}
                user_id={post.user_id}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublicProfile;
